import { useMemo } from "react";
import { Odontogram } from "react-odontogram";
import "react-odontogram/style.css";
import { denticaoDoDente, trocarParteDaSelecao, type Denticao } from "@bookdental/schemas";
import { fdiToOdontogramId } from "@/components/prosthesis/fdi-teeth";
import { ArcadaDecidua, type CondicaoDoDente } from "./ArcadaDecidua";
import { TooltipDoDente } from "./TooltipDoDente";

/** Grupo de dentes com a mesma cor (em FDI) — o plano de tratamento pinta por procedimento. */
export type CondicaoDaArcada = {
  dentes: string[];
  preenchimento: string;
  contorno: string;
  rotulo?: string;
};

/**
 * Uma arcada, permanente ou decídua, com a mesma API: `selecionados` é a lista FDI inteira (as duas
 * dentições); mexer na arcada visível troca só a parte dela. A permanente é a biblioteca
 * (não-controlada: `chave` remonta com a seleção nova); a decídua é desenho nosso, controlado.
 */
export function Arcada({
  denticao,
  selecionados,
  chave = 0,
  condicoes = [],
  corDaSelecao = null,
  somenteLeitura = false,
  onSelecionar,
}: {
  denticao: Denticao;
  selecionados: string[];
  chave?: number;
  condicoes?: CondicaoDaArcada[];
  corDaSelecao?: string | null;
  somenteLeitura?: boolean;
  onSelecionar?: (lista: string[]) => void;
}) {
  const daDenticao = selecionados.filter((d) => denticaoDoDente(d) === denticao);

  const mapaDecidua = useMemo(() => {
    const m = new Map<string, CondicaoDoDente>();
    for (const c of condicoes)
      for (const d of c.dentes) m.set(d, { preenchimento: c.preenchimento, contorno: c.contorno });
    return m;
  }, [condicoes]);
  const gruposPermanente = useMemo(
    () =>
      condicoes
        .map((c, i) => ({
          label: c.rotulo ?? String(i),
          teeth: c.dentes.filter((d) => denticaoDoDente(d) === "permanente").map(fdiToOdontogramId),
          fillColor: c.preenchimento,
          outlineColor: c.contorno,
        }))
        .filter((c) => c.teeth.length > 0),
    [condicoes],
  );

  if (denticao === "decidua") {
    return (
      <TooltipDoDente>
        <ArcadaDecidua
          selecionados={daDenticao}
          condicoes={mapaDecidua}
          corDaSelecao={corDaSelecao}
          somenteLeitura={somenteLeitura}
          onAlternar={(fdi) =>
            onSelecionar?.(selecionados.includes(fdi) ? selecionados.filter((d) => d !== fdi) : [...selecionados, fdi])
          }
        />
      </TooltipDoDente>
    );
  }
  return (
    <TooltipDoDente>
      <Odontogram
        key={chave}
        defaultSelected={daDenticao.map(fdiToOdontogramId)}
        onChange={(sel) =>
          onSelecionar?.(
            trocarParteDaSelecao(
              selecionados,
              "permanente",
              sel.map((t) => t.notations.fdi),
            ),
          )
        }
        teethConditions={gruposPermanente}
        readOnly={somenteLeitura}
        colors={corDaSelecao ? { darkBlue: corDaSelecao, lightBlue: `${corDaSelecao}66` } : undefined}
        // Tooltip da biblioteca desligado (aparece longe do dente); o número vem do TooltipDoDente.
        showTooltip={false}
      />
    </TooltipDoDente>
  );
}
